import { motion } from "framer-motion";
import { X, Check, BookX, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LANDING } from "@/constants/landingTestIds";

const OLD_WAY = [
  "Lead details scattered across paper diaries",
  "Forgotten follow-ups & missed site visits",
  "No visibility into your sales pipeline",
  "Hours wasted building property listings manually",
];

const NEW_WAY = [
  "Every lead captured & organized automatically",
  "Smart AI reminds & follows up for you",
  "Real-time visual pipeline, always up to date",
  "Property landing pages built in seconds",
];

const StorySection = () => {
  return (
    <section id="story" className="bg-[#F8FAFC] py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#2563EB]">
            The Story
          </span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A]">
            Your business deserves more than a notebook
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-500 leading-relaxed">
            Every day, thousands of real estate agents lose deals because a lead's number
            was scribbled on a page they can't find. Tracre One replaces the paper diary
            with a smart CRM that lives in your pocket.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl border border-slate-200 bg-white p-8 sm:p-10 relative overflow-hidden"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center">
                <BookX className="w-5 h-5 text-slate-400" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-400">
                The Old Way
              </h3>
            </div>
            <ul className="space-y-5">
              {OLD_WAY.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-300 shrink-0 mt-0.5" />
                  <span className="text-slate-400 line-through decoration-slate-300">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-2xl border border-[#2563EB]/20 bg-[#0F172A] p-8 sm:p-10 relative overflow-hidden shadow-xl"
          >
            <div className="absolute -top-16 -right-16 w-56 h-56 bg-[#2563EB]/30 rounded-full blur-[90px]" />
            <div className="relative flex items-center gap-3 mb-8">
              <div className="w-11 h-11 rounded-xl bg-[#2563EB]/20 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-[#38bdf8]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white">
                The Tracre One Way
              </h3>
            </div>
            <ul className="relative space-y-5">
              {NEW_WAY.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#2dd4bf]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-[#2dd4bf]" />
                  </div>
                  <span className="text-slate-200">{item}</span>
                </li>
              ))}
            </ul>
            <Button
              data-testid={LANDING.storySectionDownloadBtn}
              onClick={() =>
                document.querySelector("#download")?.scrollIntoView({ behavior: "smooth" })
              }
              className="relative mt-9 w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg h-12 font-semibold transition-all duration-300"
            >
              Switch to Tracre One
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default StorySection;
